import { skillsApi, userApi } from 'api';
import { makeAutoObservable, runInAction } from 'mobx';
import { Socket } from 'socket.io-client';
import { ExctractResults, SkillsStore } from 'types/skills';
import { personalDataStore } from './personalDataStore';
import { Position } from 'types/api';
import { omit } from 'lodash';
import {
  ExtractSkillsFailedPayload,
  ExtractSkillsReadyPayload,
  JOB_TYPES,
  wsJobFailedEvent,
  wsJobReadyEvent,
} from 'types/job';

/**
 * Включить асинхронный поток извлечения компетенций (через auth-прокси).
 * По умолчанию старый прямой запрос в core. Включается env-флагом.
 */
const USE_ASYNC_EXTRACT_SKILLS = process.env.REACT_APP_USE_ASYNC_EXTRACT_SKILLS === 'true';

/** localStorage-ключ recovery для конкретного extract-interview. */
const pendingExtractSkillsKey = (extractInterviewId: string) =>
  `extract.skills.pending.${extractInterviewId}`;

class SkillStoreImpl implements SkillsStore {
  loading = false;
  error: string | null = null;
  skills: string[] = [];
  skillsWithGrade: Record<string, number> = {};
  questions: { text: string; title: string }[] = [];
  questionIndex = 0;
  sendedVideosCount = 0;
  interviewId: string | null = null;
  authInterviewId: string | null = null;
  fetchExtractResultsImmediately = false; 
  isDesciptionViewed = false;
  extractResults = { sections: [], summary: '', final_result: { status: '', label: '' }, overall_conclusion: { text: '' } } as Omit<
    ExctractResults,
    'competences'
  > | null;

  constructor() {
    makeAutoObservable(this);
    personalDataStore.attachSkillsStore(this);
  }

  async extractCompetences(position: Position, vacancyId?: string) {
    this.loading = true;
    this.error = null;
    const res = await skillsApi.extractCompetences(position);
    const { id } = await userApi.setExtractInterview(personalDataStore.data.id as string, {
      ...omit(res, 'id'),
      interview_id: res.id,
      vacancyId: vacancyId
    });

    runInAction(() => {
      this.authInterviewId = id;
      this.questions = res.questions;
      this.interviewId = res.id;
      this.loading = false;
    });
  }

  setQuestionIndex(index: number) {
    this.questionIndex = index;
  }

  /**
   * Извлечь компетенции из видеоответов.
   *
   * Две ветки (флаг REACT_APP_USE_ASYNC_EXTRACT_SKILLS):
   *  - false: старый прямой GET в core + PATCH в auth.
   *  - true:  POST /interviews/users/extract/:id/skills → WS-событие.
   *
   * Caller (ExtractSkills.tsx) ждёт результат через `await`, поэтому
   * в async-ветке возвращаем Promise, который резолвится по WS-событию.
   *
   * @param coreInterviewId — id, который core ожидает в URL (skillsStore.interviewId).
   * @param socket — нужен в async-режиме; передаётся из page через useSocketContext.
   */
  async getSkills(coreInterviewId: string, socket?: Socket | null): Promise<ExctractResults | undefined> {
    try {
      // if (USE_ASYNC_EXTRACT_SKILLS && socket && this.authInterviewId) {
      //   return await this.runAsyncGetSkills(coreInterviewId, socket);
      // }
      if(!socket) return
      this.sendedVideosCount = 0
      return await this.runAsyncGetSkills(coreInterviewId, socket);

      // СТАРЫЙ ПОТОК
      // const res = await skillsApi.extractSkills(coreInterviewId);
      // await userApi.updateExtractInterview(this.authInterviewId as string, { results: res });
      // runInAction(() => {
      //   this.skills = res.competences || [];
      //   this.skillsExtracted = true;
      //   this.extractResults = omit(res, 'competences');
      // });
      // return res;
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Неизвестная ошибка';
      });
      return undefined;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /**
   * Async-ветка: ставит job на auth, подписывается на WS, резолвится
   * по событию `extract.skills.ready` (или reject'ится по `.failed`).
   *
   * Также проверяет pending в localStorage — если до этого был запуск,
   * который не успел дойти до WS-события (F5/обрыв), подхватываем его
   * через GET /jobs/:id вместо нового POST.
   */
  private async runAsyncGetSkills(
    coreInterviewId: string,
    socket: Socket,
  ): Promise<ExctractResults | undefined> {
    const extractInterviewId = this.authInterviewId as string;

    // Recovery: pending job
    const existingJobId = localStorage.getItem(pendingExtractSkillsKey(extractInterviewId));
    if (existingJobId) {
      try {
        const job = await userApi.getJob(existingJobId);
        if (job.status === 'DONE') {
          return this.applyExtractSkillsResult({
            jobId: existingJobId,
            extractInterviewId,
            result: job.result as any,
          });
        }
        if (job.status === 'FAILED') {
          localStorage.removeItem(pendingExtractSkillsKey(extractInterviewId));
          throw new Error(job.error || 'Не удалось извлечь компетенции');
        }
        // ещё в работе — просто подпишемся ниже
      } catch {
        // если /jobs/:id отдал 404 — снимем pending и пойдём по новому POST
        localStorage.removeItem(pendingExtractSkillsKey(extractInterviewId));
      }
    }

    let jobId = localStorage.getItem(pendingExtractSkillsKey(extractInterviewId));
    if (!jobId) {
      const { jobId: newJobId } = await userApi.startExtractSkillsJob(
        extractInterviewId,
        coreInterviewId,
      );
      jobId = newJobId;
      localStorage.setItem(pendingExtractSkillsKey(extractInterviewId), jobId);
    }

    return await this.waitForExtractSkillsJob(socket, jobId, extractInterviewId);
  }

  /**
   * Подписка на WS-события конкретной джобы. Промис резолвится первым
   * подходящим событием (по jobId) и снимает свои listener'ы.
   */
  private waitForExtractSkillsJob(
    socket: Socket,
    jobId: string,
    extractInterviewId: string,
  ): Promise<ExctractResults | undefined> {
    const readyEvent = wsJobReadyEvent(JOB_TYPES.EXTRACT_SKILLS);
    const failedEvent = wsJobFailedEvent(JOB_TYPES.EXTRACT_SKILLS);

    return new Promise((resolve, reject) => {
      const onReady = (payload: ExtractSkillsReadyPayload) => {
        if (payload.jobId !== jobId) return;
        cleanup();
        resolve(this.applyExtractSkillsResult(payload));
      };
      const onFailed = (payload: ExtractSkillsFailedPayload) => {
        if (payload.jobId !== jobId) return;
        cleanup();
        localStorage.removeItem(pendingExtractSkillsKey(extractInterviewId));
        runInAction(() => {
          this.error = payload.error || 'Не удалось извлечь компетенции';
        });
        reject(new Error(payload.error || 'Не удалось извлечь компетенции'));
      };
      const cleanup = () => {
        socket.off(readyEvent, onReady);
        socket.off(failedEvent, onFailed);
      };

      socket.on(readyEvent, onReady);
      socket.on(failedEvent, onFailed);
    });
  }

  /**
   * Достаёт ExctractResults из payload (это ExtractInterview из БД с полем .results),
   * обновляет стор и чистит pending. Возвращает результат для caller'а.
   */
  private applyExtractSkillsResult(
    payload: ExtractSkillsReadyPayload,
  ): ExctractResults | undefined {
    const results = (payload.result?.results ?? null) as ExctractResults | null;
    localStorage.removeItem(pendingExtractSkillsKey(payload.extractInterviewId));
    if (!results) return undefined;
    runInAction(() => {
      this.skills = results.competences || [];
      this.extractResults = omit(results, 'competences') as Omit<ExctractResults, 'competences'>;
    });
    return results;
  }

  async setSkillsWithGrade(skill: string, grade: number) {
    const { id } = personalDataStore.data;

    if (!id) return;
    this.skillsWithGrade[skill] = grade;
    await userApi.setCompetenceScore({ name: skill, score: grade }, id);
  }

  async getSkillsWithGrade() {
    const { id } = personalDataStore.data;

    if (!id) return;
    const scores = await userApi.getCompetenceScore(id);
    this.skillsWithGrade = scores.reduce((acc, score) => {
      acc[score.name] = score.score;
      return acc;
    }, {} as Record<string, number>);
  }

  /**
   * Отправить видеоответ на конкретный вопрос.
   *
   * `answeredIndex`/`totalQuestions` передаются явно из компонента, чтобы не
   * читать мутабельный this.questionIndex асинхронно (иначе гонка с переходом
   * к следующему вопросу даёт неверный сохранённый index). Сохраняем index
   * СЛЕДУЮЩЕГО вопроса (answeredIndex + 1), чтобы после F5 не показывать уже
   * отвеченный вопрос повторно.
   */
  async sendAnswer(
    data: File | Blob,
    answeredIndex: number,
    totalQuestions: number,
  ): Promise<boolean> {
    this.loading = true;
    this.error = null;

    try {
      await skillsApi.sendVideo(this.interviewId as string, data);
      this.sendedVideosCount = this.sendedVideosCount + 1;

      const isAnswered = answeredIndex + 1 >= totalQuestions;
      await userApi.updateExtractInterview(this.authInterviewId as string, {
        index: answeredIndex + 1,
        isAnswered,
      });
      await personalDataStore.getExtractInterviews();
      return true;
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Произошла ошибка при поытке отправить видео';
      });
      return false;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }
}

export const skillsStore = new SkillStoreImpl();
